import React, { useCallback, useState } from 'react';
import { Box, CircularProgress, MenuItem, Select, TextField, Tooltip, Typography } from '@mui/material';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import { styled } from '@mui/material/styles';
import FormProvider from 'src/components/hook-form';
import { useForm } from 'react-hook-form';
import { axiosGetCall, axiosPostCall } from 'src/services/apisCall';
import InfoIcon from '@mui/icons-material/Info';
import Logger from 'src/lib/Logger';
import axiosIntance from 'src/lib/axios';
import UploadArea from 'src/components/upload/upload-area';

const FieldHeading = styled(Typography)(() => ({
  fontWeight: 500,
  fontSize: '0.875rem',
  lineHeight: '1.43',
  marginLeft: '0.25rem',
}));

const BulkAnnouncementForm = ({ setOpenBulkModal }) => {

  const logger = new Logger('BulkAnnouncementForm');

  const [bulkTemplateFile, setBulkTemplateFile] = useState(null);
  const [uploadErrors, setUploadErrors] = useState(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [user, setUser] = useState("");
  const [routes, setRoutes] = useState("");


  const announcementRoutes = [
    { label: 'Transactional', value: 'transactional' },
    { label: 'Promotional', value: 'promotional' },
  ];

  const methods = useForm({
    defaultValues: {
    },
  });

  const { handleSubmit, reset } = methods;

  const handleDownloadErrors = () => {
    if (!uploadErrors || uploadErrors.length === 0) return;

    const headers = ['file', 'Error'];
    const csvContent = [
      headers.join(','),
      ...uploadErrors.map(err => `"${err.file}","${err.error}"`)
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'announcement_upload_errors.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = async () => {
    try {

      const data = await axiosIntance.get("v1/voice/downloadSampleVoiceZip", {
        responseType: "blob",
      });

      const blob = new Blob([data.data], {
        type: "application/zip",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "voice_sample.zip";

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      Logger.error("Announcement add bulk api", error);
      renderErrorMessage(error?.message);
    }
  };

  const handleFileChange = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];

      if (file) {
        const newFile = Object.assign(file, {
          preview: URL.createObjectURL(file),
        });
        setBulkTemplateFile(newFile);
      }
    },
    [setBulkTemplateFile]
  );

  const onSubmit = async (data) => {
    if (!user) {
      renderErrorMessage("Please enter user.");
      return;
    }
    if (!routes) {
      renderErrorMessage("Please select route.");
      return;
    }
    if (!bulkTemplateFile) {
      renderErrorMessage("Please upload a zip file.");
      return;
    }
    if (bulkTemplateFile.size === 0) {
      renderErrorMessage("File is empty. Please select a valid file.");
      return;
    }
    
    const formData = new FormData();
    formData.append('userId', user);
    formData.append('route', routes);
    formData.append('zipFile', bulkTemplateFile);
    formData.append('transcriptions', "");
    formData.append('titles', "");
    setLoadingSubmit(true);

    try {
      const response = await axiosPostCall('uploadBulkAnnouncement', formData);
      renderSuccessMessage(response.message || 'Bulk templates uploaded successfully!');
      setBulkTemplateFile(null);
      setUploadErrors(null);
      if (response?.results?.failedCount > 0) {
        setUploadErrors(response?.results?.failures);
      } else {
        setOpenBulkModal(false);
        reset();
      }
    } catch (error) {
      if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        setUploadErrors(error.response.data.errors);
        renderErrorMessage(error.message);
      } else {
        setUploadErrors(null);
        renderErrorMessage(error.message || 'Failed to upload bulk templates.');
      }
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box>
          <FieldHeading>User</FieldHeading>
          {/* <RHFTextField
            name="user"
            placeholder="Enter user"
            size="small"
          /> */}
          <TextField
            id="outlined-basic"
            variant="outlined"
            placeholder="Enter the user ID for whom you want to upload this announcement file"
            value={user}
            required
            onChange={(e) => setUser(e.target.value)}
            fullWidth
          />
        </Box>
        <Box>
          <FieldHeading>Route</FieldHeading>
          <Select
            id="routes"
            value={routes}
            size="medium"
            required="true"
            onChange={(e) => setRoutes(e.target.value)}
            fullWidth
          >
            <MenuItem value="">
              <em>Select Route</em>
            </MenuItem>
            {announcementRoutes.map(({ label, value }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2
        }}
        >
          <FieldHeading>Announcement</FieldHeading>
          <Tooltip
            placement="right"
            arrow
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 2,
                    fontSize: "0.75rem",
                    "& li": {
                      mb: 0.5,
                    },
                  }}
                >
                  <li>The title will be automatically taken from the announcement file name inside the ZIP file.</li>
                  <li>The title or announcement file name inside the ZIP file cannot exceed 50 characters.</li>
                </Typography>
              </Box>
            }
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              <InfoIcon fontSize="small" />
            </Box>
          </Tooltip>
        </Box>
        <Box>
          <Typography variant="body2" color="textSecondary" sx={{ display: 'inline' }}>
            Upload a zip file containing your announcement as per the sample zip file below
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'primary',
                cursor: 'pointer',
                fontWeight: 600,
                textDecoration: 'underline'
              }}
              onClick={handleDownload}
            >
              Download Sample Zip
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            width: '100%',
            height: '150px',
          }}
        >
          <UploadArea
          onDrop={handleFileChange}
          acceptedMimes={{
            'application/zip': ['.zip'],
            'application/x-zip-compressed': ['.zip'],
            'application/octet-stream': ['.zip'],
          }}
          sx={{ width: '100%', height: '100%' }}
        />
        </Box>
        {uploadErrors && (
          <Typography
            variant="body2"
            sx={{
              color: 'error.main',
              cursor: 'pointer',
              fontWeight: 600,
              textDecoration: 'underline'
            }}
            onClick={handleDownloadErrors}
          >
            Download Error Report
          </Typography>
        )}
        <Box
          sx={{
            width: '10rem',
            backgroundColor: !loadingSubmit ? 'primary.main' : '#8590A2',
            borderRadius: '8px',
            p: '0.562rem',
            height: '2.375rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            pointerEvents: !loadingSubmit ? 'auto' : 'none',
          }}
          onClick={handleSubmit(onSubmit)}
        >
          {!loadingSubmit && (
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                lineHeight: '1.43',
                color: '#fff',
              }}
            >
              Submit
            </Typography>
          )}
          {loadingSubmit && (
            <CircularProgress
              color="inherit"
              size={14}
              sx={{ color: '#fff' }}
            />
          )}
        </Box>
      </Box>
    </FormProvider>
  );
};
export default BulkAnnouncementForm;
